import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Container,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    ArrowBack,
    Visibility,
    Delete,
    ClearAll
} from '@mui/icons-material';
import ProductCard from '../components/ProductCard';
import useViewedProducts from '../hooks/useViewedProducts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getViewedProducts } from '../api/apiClient';

const ViewedProductsPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    // Устанавливаем заголовок страницы
    useDocumentTitle('Просмотренные товары');
    
    const { viewedProducts, clearViewedProducts, removeViewedProduct } = useViewedProducts();
    const [viewedItems, setViewedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadViewedProducts = useCallback(async () => {
        if (viewedProducts.length === 0) {
            setViewedItems([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            
            // Получаем список ID товаров
            const productIds = viewedProducts.map(item => item.spuId);
          
            
            const { data } = await getViewedProducts(productIds);
            
            if (data.success && data.products) {
              
                
                // Преобразуем данные в нужный формат
                const itemsWithData = data.products.map(item => {
                    // Подготавливаем данные для ProductCard
                    const preparedProduct = {
                        ...item.product,
                        // Убеждаемся, что у нас есть все необходимые поля
                        name: item.product.name || 'Название недоступно',
                        displayReference: item.product.displayReference || 'Артикул недоступен',
                        colors: item.product.colors || [],
                        // Добавляем поле для отображения изображения и цены
                        mainImage: item.displayImage,        // ProductCard ожидает mainImage
                        price: item.displayPrice            // ProductCard ожидает price
                    };
                    
                    const itemWithData = {
                        ...item,
                        product: preparedProduct,
                        displayImage: item.displayImage,
                        displayPrice: item.displayPrice,
                        country: item.country,
                        viewedAt: new Date(viewedProducts.find(vp => vp.spuId === item.spuId)?.viewedAt || Date.now()).toLocaleString('ru-RU')
                    };
                    
                  
                    return itemWithData;
                });
                
                setViewedItems(itemsWithData);
            } else {
              
                setViewedItems([]);
            }
            
        } catch (error) {
            console.error('❌ Error loading viewed products:', error);
            setError('Ошибка загрузки просмотренных товаров');
            setViewedItems([]);
        } finally {
            setLoading(false);
        }
    }, [viewedProducts]);

    useEffect(() => {
        loadViewedProducts();
    }, [loadViewedProducts]);

    const handleClearAll = () => {
        if (window.confirm('Вы уверены, что хотите очистить все просмотренные товары?')) {
            clearViewedProducts();
        }
    };

    const handleRemoveItem = (spuId) => {
        removeViewedProduct(spuId);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) {
            return 'Сегодня';
        } else if (diffDays === 2) {
            return 'Вчера';
        } else if (diffDays <= 7) {
            return `${diffDays - 1} дней назад`;
        } else {
            return date.toLocaleDateString('ru-RU');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress size={isMobile ? 40 : 60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
                <Alert severity="error" sx={{ mt: isMobile ? 2 : 4 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{ 
                        mb: isMobile ? 1.5 : 2,
                        fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                >
                    Назад
                </Button>
                
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'flex-start' : 'center', 
                    mb: isMobile ? 1.5 : 2,
                    gap: isMobile ? 1 : 0
                }}>
                    <Typography 
                        variant={isMobile ? "h5" : "h4"} 
                        component="h1"
                        sx={{ mb: isMobile ? 1 : 0 }}
                    >
                        Просмотренные товары
                    </Typography>
                    
                    {viewedItems.length > 0 && (
                        <Button
                            startIcon={<ClearAll />}
                            onClick={handleClearAll}
                            variant="outlined"
                            color="error"
                            size={isMobile ? "small" : "medium"}
                        >
                            Очистить все
                        </Button>
                    )}
                </Box>
                
                {viewedItems.length > 0 && (
                    <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                    >
                        Всего просмотрено: {viewedItems.length} товар{viewedItems.length === 1 ? '' : viewedItems.length < 5 ? 'а' : 'ов'}
                    </Typography>
                )}
            </Box>

            {viewedItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: isMobile ? 4 : 8 }}>
                    <Visibility sx={{ 
                        fontSize: isMobile ? 60 : 80, 
                        color: 'text.secondary', 
                        mb: isMobile ? 1.5 : 2 
                    }} />
                    <Typography 
                        variant={isMobile ? "h6" : "h6"} 
                        color="text.secondary" 
                        gutterBottom
                        sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                    >
                        Нет просмотренных товаров
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ 
                            mb: isMobile ? 2 : 3,
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            px: isMobile ? 2 : 0
                        }}
                    >
                        Просматривайте товары, чтобы они появились здесь
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/')}
                        size={isMobile ? "medium" : "large"}
                        sx={{ px: isMobile ? 3 : 4 }}
                    >
                        Перейти к товарам
                    </Button>
                </Box>
            ) : (
                <Box 
                    className="product-grid"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile 
                            ? '1fr' 
                            : isTablet 
                                ? 'repeat(2, 1fr)' 
                                : 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: isMobile ? 2 : 3,
                        '& .product-card': {
                            width: '100%'
                        }
                    }}
                >
                    {viewedItems.map((item, index) => (
                        <Box key={`${item.spuId}-${index}`} className="product-card" sx={{ position: 'relative' }}>

                                    {/* Бейдж времени просмотра */}
                                    <Box sx={{ 
                                        position: 'absolute', 
                                        top: isMobile ? 6 : 8, 
                                        right: isMobile ? 6 : 8, 
                                        zIndex: 1 
                                    }}>
                                        <Chip
                                            label={formatDate(item.viewedAt)}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                            sx={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                height: isMobile ? '20px' : '24px',
                                                '& .MuiChip-label': {
                                                    px: isMobile ? 1 : 1.5
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Кнопка удаления */}
                                    <Box sx={{ 
                                        position: 'absolute', 
                                        top: isMobile ? 6 : 8, 
                                        left: isMobile ? 6 : 8, 
                                        zIndex: 1 
                                    }}>
                                        <IconButton
                                            size={isMobile ? "small" : "small"}
                                            onClick={() => handleRemoveItem(item.spuId)}
                                            sx={{ 
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                width: isMobile ? 32 : 36,
                                                height: isMobile ? 32 : 36,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 1)'
                                                }
                                            }}
                                        >
                                            <Delete fontSize={isMobile ? "small" : "small"} />
                                        </IconButton>
                                    </Box>

                                    <ProductCard
                                        product={item.product}
                                        onClick={() => navigate(`/product/${item.spuId}`)}
                                        showColors={true}
                                        showFavorite={false}
                                        showNewBadge={false}
                                        variant="default"
                                    />
                            </Box>
                        ))}
                    </Box>
            )}
        </Container>
    );
};

export default ViewedProductsPage;
